import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Save, Percent, Wallet, Fuel, Settings2, KeyRound, MapPin, Boxes, Truck, Scissors, Users, Trash2 } from "lucide-react";

export default function VanSettings() {
  const [s, setS] = useState(null);
  const [personnel, setPersonnel] = useState([]);
  const [busy, setBusy] = useState(false);
  const [dedTpls, setDedTpls] = useState([]);
  const [tplName, setTplName] = useState("");

  useEffect(() => {
    api.get("/van-sales/settings").then((r) => setS(r.data)).catch(() => {});
    api.get("/accounting/personnel", { params: { status: "all" } })
      .then((r) => setPersonnel(r.data?.items || [])).catch(() => {});
    api.get("/van-sales/deduction-templates").then((r) => setDedTpls(r.data?.templates || [])).catch(() => {});
  }, []);

  if (!s) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>;

  const set = (grp, key, val) => setS({ ...s, [grp]: { ...s[grp], [key]: val } });
  const num = (v) => (v === "" || v == null ? 0 : Number(v));

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        discount: {
          allowed_rates: String(s.discount.allowed_rates || "").toString().split(",").map((x) => Number(String(x).trim())).filter((x) => x > 0),
          weekly_rights: num(s.discount.weekly_rights),
          block_same_customer_same_day: !!s.discount.block_same_customer_same_day,
        },
        pricing: { max_markup_ceiling_pct: num(s.pricing.max_markup_ceiling_pct) },
        commissions: {
          driver_prim_pct: num(s.commissions.driver_prim_pct),
          warehouse_manager_pct: num(s.commissions.warehouse_manager_pct),
          sales_manager_pct: num(s.commissions.sales_manager_pct),
          warehouse_profit_pct: num(s.commissions.warehouse_profit_pct),
          warehouse_manager_personnel_id: s.commissions.warehouse_manager_personnel_id || null,
          sales_manager_personnel_id: s.commissions.sales_manager_personnel_id || null,
        },
        expenses: {
          pos_commission_pct: num(s.expenses.pos_commission_pct),
          default_fuel: num(s.expenses.default_fuel),
          default_allowance: num(s.expenses.default_allowance),
        },
        deductions: {
          fuel: s.deductions?.fuel !== false,
          allowance: s.deductions?.allowance !== false,
          pos_commission: s.deductions?.pos_commission !== false,
          warehouse_manager: s.deductions?.warehouse_manager !== false,
          sales_manager: s.deductions?.sales_manager !== false,
          warehouse_profit: s.deductions?.warehouse_profit !== false,
        },
        ignition: {
          enabled: !!s.ignition?.enabled,
          target_time: s.ignition?.target_time || "08:30",
          grace_min: num(s.ignition?.grace_min),
          on_time_points: num(s.ignition?.on_time_points),
          late_points_per_block: num(s.ignition?.late_points_per_block),
          late_block_min: num(s.ignition?.late_block_min) || 15,
          max_late_points: num(s.ignition?.max_late_points),
          geofence: {
            enabled: !!s.ignition?.geofence?.enabled,
            radius_m: num(s.ignition?.geofence?.radius_m) || 300,
          },
        },
        regions: { enforce: !!s.regions?.enforce },
        stock_request: { low_stock_pct: num(s.stock_request?.low_stock_pct) },
      };
      const { data } = await api.put("/van-sales/settings", payload);
      setS(data);
      toast.success("Rota ayarları kaydedildi");
    } catch (e) { toast.error(e?.response?.data?.detail || "Kaydedilemedi"); }
    finally { setBusy(false); }
  };

  const loadDedTpls = () => api.get("/van-sales/deduction-templates").then((r) => setDedTpls(r.data?.templates || [])).catch(() => {});
  const curDeductions = () => ({
    fuel: s.deductions?.fuel !== false, allowance: s.deductions?.allowance !== false,
    pos_commission: s.deductions?.pos_commission !== false, warehouse_manager: s.deductions?.warehouse_manager !== false,
    sales_manager: s.deductions?.sales_manager !== false, warehouse_profit: s.deductions?.warehouse_profit !== false,
  });
  const saveDedTpl = async () => {
    const name = tplName.trim();
    if (!name) { toast.error("Şablon adı girin"); return; }
    try { await api.post("/van-sales/deduction-templates", { name, deductions: curDeductions() }); toast.success("Kesinti şablonu kaydedildi"); setTplName(""); loadDedTpls(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Kaydedilemedi"); }
  };
  const applyDedTpl = async (t) => {
    try { const { data } = await api.post(`/van-sales/deduction-templates/${t.id}/apply`); setS((st) => ({ ...st, deductions: data.deductions })); toast.success(`'${t.name}' şablonu uygulandı`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Uygulanamadı"); }
  };
  const delDedTpl = async (t) => {
    try { await api.delete(`/van-sales/deduction-templates/${t.id}`); loadDedTpls(); toast.info("Şablon silindi"); }
    catch (e) { toast.error("Silinemedi"); }
  };

  const F = ({ label, children }) => (
    <div><Label className="text-xs text-slate-500">{label}</Label><div className="mt-1">{children}</div></div>
  );
  const Card = ({ icon: Icon, title, children }) => (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800"><Icon className="h-4 w-4 text-brand-green" /> {title}</div>
      <div className="grid gap-3 sm:grid-cols-2">{children}</div>
    </div>
  );

  const _dedKeys = ["fuel", "allowance", "pos_commission", "warehouse_manager", "sales_manager", "warehouse_profit"];
  const _dedEq = (a, b) => _dedKeys.every((k) => (a?.[k] !== false) === (b?.[k] !== false));
  const activeDedTpl = dedTpls.find((t) => _dedEq(t.deductions, s.deductions));

  return (
    <div className="max-w-3xl space-y-4" data-testid="van-settings">
      <Card icon={Percent} title="İskonto Kuralları (Şoför)">
        <F label="İzinli oranlar (virgülle, ör. 3,5)">
          <Input data-testid="vset-rates" value={Array.isArray(s.discount.allowed_rates) ? s.discount.allowed_rates.join(",") : s.discount.allowed_rates}
            onChange={(e) => set("discount", "allowed_rates", e.target.value)} />
        </F>
        <F label="Haftalık iskonto hakkı (adet)">
          <Input type="number" data-testid="vset-weekly" value={s.discount.weekly_rights} onChange={(e) => set("discount", "weekly_rights", e.target.value)} />
        </F>
        <label className="flex items-center gap-2 text-sm text-slate-600 sm:col-span-2">
          <input type="checkbox" data-testid="vset-samecust" checked={!!s.discount.block_same_customer_same_day} onChange={(e) => set("discount", "block_same_customer_same_day", e.target.checked)} />
          Aynı müşteriye aynı gün tekrar iskonto engellensin
        </label>
      </Card>

      <Card icon={Settings2} title="Fiyat Tavanı">
        <F label="Pazaryeri fiyatının % altı tavan (zam üst sınırı)">
          <Input type="number" data-testid="vset-ceiling" value={s.pricing.max_markup_ceiling_pct} onChange={(e) => set("pricing", "max_markup_ceiling_pct", e.target.value)} />
        </F>
        <p className="self-end text-xs text-slate-400">Şoför fiyatı Pazaryeri fiyatının bu % altına kadar yükseltebilir. Rota fiyatı altına inemez.</p>
      </Card>

      <Card icon={Wallet} title="Prim & Komisyonlar">
        <F label="Şoför primi (net kârın %'si)"><Input type="number" data-testid="vset-prim" value={s.commissions.driver_prim_pct} onChange={(e) => set("commissions", "driver_prim_pct", e.target.value)} /></F>
        <F label="Depo karı (% ciro)"><Input type="number" value={s.commissions.warehouse_profit_pct} onChange={(e) => set("commissions", "warehouse_profit_pct", e.target.value)} /></F>
        <F label="Depo sorumlusu komisyonu (% ciro)"><Input type="number" value={s.commissions.warehouse_manager_pct} onChange={(e) => set("commissions", "warehouse_manager_pct", e.target.value)} /></F>
        <F label="Pazarlama müdürü komisyonu (% ciro)"><Input type="number" value={s.commissions.sales_manager_pct} onChange={(e) => set("commissions", "sales_manager_pct", e.target.value)} /></F>
        <F label="Depo sorumlusu (komisyon alacak personel)">
          <select data-testid="vset-whmgr" className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
            value={s.commissions.warehouse_manager_personnel_id || ""} onChange={(e) => set("commissions", "warehouse_manager_personnel_id", e.target.value || null)}>
            <option value="">— Seçilmedi —</option>
            {personnel.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </F>
        <F label="Pazarlama müdürü (komisyon alacak personel)">
          <select data-testid="vset-smgr" className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
            value={s.commissions.sales_manager_personnel_id || ""} onChange={(e) => set("commissions", "sales_manager_personnel_id", e.target.value || null)}>
            <option value="">— Seçilmedi —</option>
            {personnel.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </F>
      </Card>

      <Card icon={Fuel} title="Gider Varsayılanları">
        <F label="POS komisyon oranı (% kart tahsilatı)"><Input type="number" data-testid="vset-poscomm" value={s.expenses.pos_commission_pct} onChange={(e) => set("expenses", "pos_commission_pct", e.target.value)} /></F>
        <F label="Varsayılan yakıt gideri (₺)"><Input type="number" value={s.expenses.default_fuel} onChange={(e) => set("expenses", "default_fuel", e.target.value)} /></F>
        <F label="Varsayılan yol-yemek (₺/gün)"><Input type="number" value={s.expenses.default_allowance} onChange={(e) => set("expenses", "default_allowance", e.target.value)} /></F>
      </Card>

      <Card icon={Scissors} title="Kesinti Kalemleri (Aç / Kapat)">
        {activeDedTpl && <div className="sm:col-span-2 -mt-1"><span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-700" data-testid="ded-active-tpl"><Scissors className="h-3 w-3" /> Aktif şablon: {activeDedTpl.name}</span></div>}
        {[
          ["fuel", "Yakıt gideri"],
          ["allowance", "Yol-Yemek (harcırah)"],
          ["pos_commission", "POS komisyonu"],
          ["warehouse_manager", "Depo sorumlusu komisyonu"],
          ["sales_manager", "Pazarlama müdürü komisyonu"],
          ["warehouse_profit", "Depo karı"],
        ].map(([k, lbl]) => (
          <label key={k} className="flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" data-testid={`vset-ded-${k}`} checked={s.deductions?.[k] !== false}
              onChange={(e) => set("deductions", k, e.target.checked)} />
            {lbl}
          </label>
        ))}
        <p className="text-xs text-slate-400 sm:col-span-2">Kapalı kalemler mutabakatta hesaba katılmaz. Kesinti kırılımını (kalem kalem) yalnız Yönetici ve Muhasebe görür; şoför/amir yalnız toplam "Kesinti" tutarını görür.</p>
        <div className="sm:col-span-2 border-t border-slate-100 pt-3" data-testid="ded-templates">
          <Label className="text-xs text-slate-500">Kesinti Şablonları (tek tıkla uygula)</Label>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <Input value={tplName} onChange={(e) => setTplName(e.target.value)} placeholder="Şablon adı (ör. Yaz Dönemi)" className="h-8 w-52" data-testid="ded-tpl-name" />
            <Button size="sm" variant="outline" onClick={saveDedTpl} data-testid="ded-tpl-save"><Save className="mr-1 h-3.5 w-3.5" /> Mevcut düzeni kaydet</Button>
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5" data-testid="ded-tpl-list">
            {dedTpls.length === 0 && <span className="text-[11px] text-slate-400">Kayıtlı şablon yok.</span>}
            {dedTpls.map((t) => (
              <span key={t.id} className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-600" data-testid={`ded-tpl-${t.id}`}>
                <button type="button" onClick={() => applyDedTpl(t)} className="font-medium hover:text-brand-green" data-testid={`ded-tpl-apply-${t.id}`}>{t.name}</button>
                <button type="button" onClick={() => delDedTpl(t)} className="text-slate-400 hover:text-red-600" data-testid={`ded-tpl-del-${t.id}`}><Trash2 className="h-3 w-3" /></button>
              </span>
            ))}
          </div>
        </div>
      </Card>

      <Card icon={KeyRound} title="Sabah Kontak / Mesai Puanı">
        <label className="flex items-center gap-2 text-sm text-slate-600 sm:col-span-2">
          <input type="checkbox" data-testid="vset-ign-enabled" checked={!!s.ignition?.enabled} onChange={(e) => set("ignition", "enabled", e.target.checked)} />
          Kontak/mesai puanlaması açık
        </label>
        <F label="Hedef kontak saati (SS:DD)"><Input data-testid="vset-ign-target" value={s.ignition?.target_time || ""} onChange={(e) => set("ignition", "target_time", e.target.value)} placeholder="08:30" /></F>
        <F label="Hoşgörü süresi (dk)"><Input type="number" data-testid="vset-ign-grace" value={s.ignition?.grace_min ?? ""} onChange={(e) => set("ignition", "grace_min", e.target.value)} /></F>
        <F label="Zamanında puan (+)"><Input type="number" data-testid="vset-ign-ontime" value={s.ignition?.on_time_points ?? ""} onChange={(e) => set("ignition", "on_time_points", e.target.value)} /></F>
        <F label="Her gecikme bloğu için puan (−)"><Input type="number" value={s.ignition?.late_points_per_block ?? ""} onChange={(e) => set("ignition", "late_points_per_block", e.target.value)} /></F>
        <F label="Gecikme bloğu (dk)"><Input type="number" value={s.ignition?.late_block_min ?? ""} onChange={(e) => set("ignition", "late_block_min", e.target.value)} /></F>
        <F label="Maksimum gecikme cezası (puan)"><Input type="number" value={s.ignition?.max_late_points ?? ""} onChange={(e) => set("ignition", "max_late_points", e.target.value)} /></F>
        <label className="flex items-center gap-2 text-sm text-slate-600 sm:col-span-2">
          <input type="checkbox" data-testid="vset-geofence-enabled" checked={!!s.ignition?.geofence?.enabled}
            onChange={(e) => setS({ ...s, ignition: { ...s.ignition, geofence: { ...(s.ignition?.geofence || {}), enabled: e.target.checked } } })} />
          Konum zorunlu kontak (şoför yalnız araç/depo lokasyonundayken kontak açabilsin — sahte mesai engellensin)
        </label>
        <F label="İzinli mesafe yarıçapı (m)"><Input type="number" data-testid="vset-geofence-radius" value={s.ignition?.geofence?.radius_m ?? ""}
          onChange={(e) => setS({ ...s, ignition: { ...s.ignition, geofence: { ...(s.ignition?.geofence || {}), radius_m: e.target.value } } })} /></F>
        <p className="self-end text-xs text-slate-400">Konum kontrolü için aracın bağlı olduğu lokasyonun koordinatı gereklidir (Saha Ziyaret → Lokasyon Ayarları).</p>
      </Card>

      <Card icon={MapPin} title="Bölge Kuralı">
        <label className="flex items-center gap-2 text-sm text-slate-600 sm:col-span-2">
          <input type="checkbox" data-testid="vset-region-enforce" checked={!!s.regions?.enforce} onChange={(e) => set("regions", "enforce", e.target.checked)} />
          Bölge kısıtı uygulansın (şoför izinli olmadığı bölgeye amir onayı olmadan satış yapamaz)
        </label>
      </Card>

      <Card icon={Boxes} title="Depo Talebi">
        <F label="Depo kritik stok uyarı eşiği (%)"><Input type="number" data-testid="vset-lowpct" value={s.stock_request?.low_stock_pct ?? ""} onChange={(e) => set("stock_request", "low_stock_pct", e.target.value)} /></F>
        <p className="self-end text-xs text-slate-400">Talep karşılandığında depo stoğu bu % altına düşecekse onay ekranında kritik uyarısı gösterilir.</p>
      </Card>

      <VehicleLocations />

      <DriverDiscounts />

      <Button onClick={save} disabled={busy} data-testid="vset-save">
        {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />} Ayarları Kaydet
      </Button>
    </div>
  );
}

function VehicleLocations() {
  const [vehicles, setVehicles] = useState([]);
  const [locs, setLocs] = useState([]);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState("");
  useEffect(() => {
    api.get("/van-sales/vehicles").then((r) => {
      setVehicles(r.data || []);
      const m = {}; (r.data || []).forEach((v) => { m[v.id] = v.location_ids || []; }); setSel(m);
    }).catch(() => {});
    api.get("/van-sales/locations").then((r) => setLocs(r.data || [])).catch(() => {});
  }, []);
  const toggle = (vid, lid) => setSel((st) => {
    const cur = new Set(st[vid] || []);
    if (cur.has(lid)) cur.delete(lid); else cur.add(lid);
    return { ...st, [vid]: [...cur] };
  });
  const save = async (v) => {
    setBusy(v.id);
    try {
      await api.put(`/van-sales/vehicles/${v.id}/locations`, { location_ids: sel[v.id] || [] });
      toast.success(`${v.plate}: lokasyon bağlantısı kaydedildi`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Kaydedilemedi"); }
    finally { setBusy(""); }
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="vset-vehicle-locations">
      <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-slate-800"><Truck className="h-4 w-4 text-brand-green" /> Rota Araçları · Mağaza/Depo Bağlantısı</div>
      <p className="mb-3 text-xs text-slate-500">Her satış aracı bir/birden fazla mağaza-depoya bağlıdır. Stok talepleri bağlı depodan hazırlanır; konum zorunlu kontak bu lokasyonlara göre denetlenir.</p>
      {vehicles.length === 0 && <p className="py-2 text-sm text-slate-400">Araç bulunamadı.</p>}
      <div className="space-y-3">
        {vehicles.map((v) => (
          <div key={v.id} className="rounded-lg border border-slate-100 p-3" data-testid={`vset-veh-${v.id}`}>
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-medium text-slate-800">{v.plate}{v.driver_name ? ` · ${v.driver_name}` : ""}</span>
              <Button size="sm" onClick={() => save(v)} disabled={busy === v.id} data-testid={`vset-veh-save-${v.id}`}>
                {busy === v.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Save className="mr-1 h-3.5 w-3.5" /> Kaydet</>}
              </Button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {locs.length === 0 && <span className="text-xs text-slate-400">Lokasyon yok</span>}
              {locs.map((l) => {
                const on = (sel[v.id] || []).includes(l.id);
                return (
                  <button key={l.id} type="button" onClick={() => toggle(v.id, l.id)} data-testid={`vset-veh-${v.id}-loc-${l.id}`}
                    className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${on ? "bg-brand-green text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>{l.name}</button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DriverDiscounts() {
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState({});
  const [busy, setBusy] = useState("");

  const load = () => api.get("/van-sales/drivers").then((r) => {
    setData(r.data);
    const m = {};
    (r.data?.drivers || []).forEach((d) => {
      m[d.id] = { rates: (d.discount?.allowed_rates || []).join(","), weekly: d.discount?.weekly_rights ?? "", custom: !!d.discount };
    });
    setEdit(m);
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  if (!data) return null;
  const base = data.base_discount || {};
  const filtered = (data.drivers || []).filter((d) => d.active_driver || (d.allowed_regions || []).length || d.discount);
  const shown = filtered.length ? filtered : (data.drivers || []);
  const upd = (id, f, v) => setEdit((st) => ({ ...st, [id]: { ...(st[id] || {}), [f]: v } }));

  const save = async (d) => {
    const e = edit[d.id] || {};
    setBusy(d.id);
    try {
      if (!e.custom) {
        await api.put(`/van-sales/drivers/${d.id}/discount`, { inherit: true });
      } else {
        const rates = String(e.rates || "").split(",").map((x) => Number(String(x).trim())).filter((x) => x > 0);
        await api.put(`/van-sales/drivers/${d.id}/discount`, { allowed_rates: rates, weekly_rights: e.weekly === "" ? null : Number(e.weekly) });
      }
      toast.success(`${d.name}: iskonto hakkı kaydedildi`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Kaydedilemedi"); }
    finally { setBusy(""); }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="vset-driver-discounts">
      <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-slate-800"><Users className="h-4 w-4 text-brand-green" /> Şoför Bazlı İskonto Hakları</div>
      <p className="mb-3 text-xs text-slate-500">
        Her şoföre farklı izinli oran ve haftalık hak tanımlayabilirsiniz. "Özel hak tanımla" kapalıyken genel kural
        (izinli: {Array.isArray(base.allowed_rates) && base.allowed_rates.length ? base.allowed_rates.map((r) => `%${r}`).join(", ") : "—"} · haftalık {base.weekly_rights ?? "—"}) geçerlidir.
      </p>
      {shown.length === 0 && <p className="py-2 text-sm text-slate-400">Şoför bulunamadı.</p>}
      <div className="space-y-2">
        {shown.map((d) => {
          const e = edit[d.id] || {};
          return (
            <div key={d.id} className="rounded-lg border border-slate-100 p-3" data-testid={`vdisc-${d.id}`}>
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm font-medium text-slate-800">{d.name}{d.code ? ` · ${d.code}` : ""}{d.discount ? "" : " (genele uyar)"}</span>
                <label className="flex items-center gap-1.5 text-xs text-slate-600">
                  <input type="checkbox" data-testid={`vdisc-custom-${d.id}`} checked={!!e.custom} onChange={(ev) => upd(d.id, "custom", ev.target.checked)} />
                  Özel hak tanımla
                </label>
              </div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                <div>
                  <Label className="text-[11px] text-slate-500">İzinli oranlar (virgülle, ör. 3,5,8)</Label>
                  <Input value={e.rates || ""} disabled={!e.custom} placeholder={Array.isArray(base.allowed_rates) ? base.allowed_rates.join(",") : "3,5"}
                    data-testid={`vdisc-rates-${d.id}`} onChange={(ev) => upd(d.id, "rates", ev.target.value)} className="mt-1 h-8" />
                </div>
                <div>
                  <Label className="text-[11px] text-slate-500">Haftalık hak (adet)</Label>
                  <Input type="number" value={e.weekly ?? ""} disabled={!e.custom} placeholder={String(base.weekly_rights ?? "")}
                    data-testid={`vdisc-weekly-${d.id}`} onChange={(ev) => upd(d.id, "weekly", ev.target.value)} className="mt-1 h-8" />
                </div>
                <div className="flex items-end">
                  <Button size="sm" onClick={() => save(d)} disabled={busy === d.id} data-testid={`vdisc-save-${d.id}`}>
                    {busy === d.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Save className="mr-1 h-3.5 w-3.5" /> Kaydet</>}
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

